import type { ChatTemplate } from "@/types/prompt-blueprint";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isObject = (value: any) => value !== null && typeof value === "object";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isObjectOrArray = (value: any) => isObject(value) || Array.isArray(value);

/**
 * Convert a record of mixed-type variable values into a record of strings.
 * Objects and arrays are JSON-stringified with pretty-printing; primitives
 * are converted with `String()`.
 */
export const stringifyVariables = (
  variables: Record<string, string | boolean | number | object>,
): Record<string, string> =>
  Object.fromEntries(
    Object.entries(variables).map(([key, value]) => [
      key,
      typeof value === "object" && value !== null ? JSON.stringify(value, null, 2) : String(value),
    ]),
  ) as Record<string, string>;

type ToolFields = Pick<
  ChatTemplate,
  "functions" | "function_call" | "tools" | "tool_choice" | "json_schema"
>;

/**
 * Compute which tool/function fields from {@link source} should be merged
 * into {@link target} — i.e. fields that the source has but the target is
 * missing (empty or falsy).  Returned values are deep-cloned.
 *
 * Returns `null` when nothing needs merging.
 */
export const getMissingToolData = (
  target: ToolFields,
  source: ToolFields,
): Partial<ToolFields> | null => {
  const result: Partial<ToolFields> = {};
  let hasChanges = false;

  if (!target.functions?.length && source.functions?.length) {
    result.functions = structuredClone(source.functions);
    hasChanges = true;
  }
  if (!target.function_call && source.function_call) {
    result.function_call = structuredClone(source.function_call);
    hasChanges = true;
  }
  if (!target.tools?.length && source.tools?.length) {
    result.tools = structuredClone(source.tools);
    hasChanges = true;
  }
  if (!target.tool_choice && source.tool_choice) {
    result.tool_choice = structuredClone(source.tool_choice);
    hasChanges = true;
  }
  if (!target.json_schema && source.json_schema) {
    result.json_schema = structuredClone(source.json_schema);
    hasChanges = true;
  }

  return hasChanges ? result : null;
};

/**
 * Merge tool/function fields from {@link source} into {@link target} **in-place**.
 *
 * Only fields that the target is missing (empty or falsy) are copied over.
 * Values from the source are deep-cloned before assignment.
 */
export const mergeToolDataIntoTemplate = (target: ChatTemplate, source: ToolFields) => {
  const merged = getMissingToolData(target, source);
  if (merged) {
    Object.assign(target, merged);
  }
};
